import ajax from '@/ajax/front_api/cart';
import EventBus from '@/event-bus';
import Shop from '@/shop';
import createStore from '@/storage';
import domReady from '@/tools/domReady';
import cookie from '@/tools/get-cookie';
import _clone from 'lodash/clone';
import _cloneDeep from 'lodash/cloneDeep';
import _concat from 'lodash/concat';
import _forEach from 'lodash/forEach';
import _forIn from 'lodash/forIn';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _isFunction from 'lodash/isFunction';
import _isObject from 'lodash/isObject';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _merge from 'lodash/merge';
import _reduce from 'lodash/reduce';
import _toFinite from 'lodash/toFinite';
import _toString from 'lodash/toString';
import _toArray from 'lodash/toArray';
import _toNumber from 'lodash/toNumber';
import ensurePath from '@/helpers/ensureObjectPath';
import haveSameKeys from '@/helpers/haveSameKeys';

const Store = createStore({ name: 'cart-insales' });

/**
 * @private
 * @class
 *
 * @description
 * Менеджер задач для корзины
 * Занимается контролем за задачами, склейкой и отправкой
 *
 * @param {Object} _owner - ссылка на корзину. -> Поменять на CartTasks
 *
 * @return {void}
 */
const CartTasks = function(_owner) {
  const self = this;

  self._owner = _owner;
  self._currency_code = Shop.config.config.currency_code;
  self._lock = false;

  self._taskToWork = [];
  self._taskInWork = [];

  domReady(function() {
    if (!Shop.config.config.is_minisite) {
      self._init();
    }
  });

  return;
};

/**
 * Обновление состава корзины. Основной способ изменеия состава. Все остальные таски забрасывают сюда
 * @method
 * @private
 *
 * @param {Object} items - список товаров для задачи
 * @param {Object} task - задача, которую применяем к корзине
 *
 * @return {void}
 */
CartTasks.prototype._update = function(items, task) {
  const self = this;

  self._before();

  // Если в таске был передан склад, то вешаем флаг что мы ждём ответа от сервера прежде чем отправить следующий запрос
  if (task.warehouse_id) {
    self.warehouse_task_waiting = true;
  }

  ajax.update(items, task)
    .done(function(response) {
      self._done(response, false, items, function() {
        self._always();
      });
    })
    .fail(function(response) {
      self._fail(response, function() {
        self._always();
      });
    })
    .always(function() {
      if (task.warehouse_id) {
        self.warehouse_task_waiting = false;
      }
    });
};

/**
 * Основной обработчик set_items
 * @method
 * @private
 *
 * @param {Object} task - выполняемая задача
 * @param {Object} currentItems - актуальный список товаров
 *
 * @return {Object} current_items - пропатченный список товаров
 */
CartTasks.prototype.set_items = function(task, currentItems) {
  const self = this;

  self.processWithAccessories(task.items, currentItems, (currentItem, taskItem, variantId) => {
    if (currentItem) {
      currentItem.quantity = _toFinite(taskItem.quantity);
    } else {
      currentItems.accessoriable_variant_ids[variantId].push({
        quantity: _toFinite(taskItem.quantity),
        accessory_value_ids: taskItem.accessory_value_ids
      });
    }
  });

  self.updateItemQuantities(task, currentItems, (currentQuantity, quantity) => _toFinite(quantity));

  return currentItems;
};

/**
 * Обработчик удаления
 * @method
 * @private
 *
 * @param {Object} task - выполняемая задача
 * @param {Object} currentItems - актуальный состав корзины
 *
 * @return {Object} current_items - пропаченный состав корзины
 */
CartTasks.prototype.delete_items = function(task, currentItems) {
  const self = this;
  const itemsArray = _toArray(task.items);

  // Обработка позиций с опциями
  self.processWithAccessories(task.items, currentItems, (currentItem) => {
    if (!currentItem) { return; }

    currentItem.quantity = 0;
  });

  itemsArray.forEach(variantId => {
    if (_isObject(variantId)) { return; }

    // Обработка таски со складами
    const hasWarehouseOrder = task.warehouse_id && currentItems.multi_orders && currentItems.multi_orders[task.warehouse_id];

    if (hasWarehouseOrder) {
      const warehouseOrderQuantity = currentItems.multi_orders[task.warehouse_id].items[variantId] || 0;
      currentItems[variantId] = (currentItems[variantId] || 0) - warehouseOrderQuantity;
      currentItems.multi_orders[task.warehouse_id].items[variantId] = 0;

      return;
    }

    // Если это обычный товар, без опций и без складов
    currentItems[variantId] = 0;
  });

  return currentItems;
};

/**
 * Обработчик очистки корзины
 * @method
 * @private
 *
 * @param {Object} task - выполняеая задача
 * @param {Object} currentItems - актуальный состав
 *
 * @return {Object} current_items - пропатченнный состав корзины, вида {variant_id: 0, variant_id: 0, ...}
 */
CartTasks.prototype.clear_items = function(task, currentItems) {
  // Если есть товары с опциями, то обнуляем их
  if (currentItems.accessoriable_variant_ids) {
    _forIn(currentItems.accessoriable_variant_ids, function(items) {
      _forEach(items, function(item) {
        item.quantity = 0;
      });
    });
  }

  // Если в таску был передан склад, то удаляем товары только с этого склада
  if (task.warehouse_id && currentItems.multi_orders && currentItems.multi_orders[task.warehouse_id]) {
    _forIn(currentItems.multi_orders[task.warehouse_id].items, function(quantity, variant_id) {
      if (currentItems[variant_id]) {
        currentItems[variant_id] = currentItems[variant_id] - currentItems.multi_orders[task.warehouse_id].items[variant_id];
      }

      currentItems.multi_orders[task.warehouse_id].items[variant_id] = 0;
    });
  } else {
    _forIn(currentItems, function(value, key) {
      if (key !== 'accessoriable_variant_ids') {
        currentItems[key] = 0;
      }
    });
  }

  return currentItems;
};

/**
 * Обработчик
 * @method
 * @private
 *
 * @param {Object} task - выполняеая задача
 * @param {Object} current_items - актуальный состав
 *
 * @return {Object} current_items - пропатченнный состав корзины, вида {variant_id: 0, variant_id: 0, ...}
 */
CartTasks.prototype.set_coupon = function(task, current_items) {
  return current_items;
};

/**
 * Обработчик для заказа в 1 клик
 * @method
 * @private
 *
 * @param {Object} task - выполняеая задача
 * @param {Object} currentItems - актуальный состав
 *
 * @return {Object} current_items - пропатченнный состав корзины, вида {variant_id: 0, variant_id: 0, ...}
 */
CartTasks.prototype.add_checkout = function(task, currentItems) {
  const self = this;

  self.processWithAccessories(task.items, currentItems, (currentItem, taskItem, variantId) => {
    if (currentItem) {
      currentItem.quantity = _toFinite(currentItem.quantity) + _toFinite(taskItem.quantity);
    } else {
      currentItems.accessoriable_variant_ids[variantId].push({
        quantity: _toFinite(taskItem.quantity),
        accessory_value_ids: taskItem.accessory_value_ids
      });
    }
  });

  self.updateItemQuantities(task, currentItems, (currentQuantity, quantity) => _toFinite(currentQuantity) + _toFinite(quantity));

  return currentItems;
};

/**
 * Обработчик задачи добавления товара
 * @method
 * @private
 *
 * @param {Object} task - исходная задача
 * @param {Object} currentItems - актуальное состояние корзины
 *
 * @return {Object} current_items - пропатченный состав корзины
 */
CartTasks.prototype.add_items = function(task, currentItems) {
  const self = this;

  self.processWithAccessories(task.items, currentItems, (currentItem, taskItem, variant_id) => {
    if (currentItem) {
      currentItem.quantity = _toFinite(currentItem.quantity) + _toFinite(taskItem.quantity);
    } else {
      currentItems.accessoriable_variant_ids[variant_id].push({
        quantity: _toFinite(taskItem.quantity),
        accessory_value_ids: taskItem.accessory_value_ids
      });
    }
  });

  self.updateItemQuantities(task, currentItems, (currentQuantity, quantity) => _toFinite(currentQuantity) + _toFinite(quantity));

  return currentItems;
};

/**
 * @todo WTF?!?!
 * @method
 *
 * @description
 * Вызывается, если кеш корзины инвалидировался. Или у нас просто инит.
 * @private
 *
 * @param {Object} task - выполняеая задача
 * @param {Object} current_items - актуальный состав
 *
 * @return {Object} current_items - пропатченнный состав корзины, вида {variant_id: 0, variant_id: 0, ...}
 */
CartTasks.prototype.get = function() {
  const current_items = {};

  return current_items;
};

/**
 * Основной обработчик удаления
 * @method
 * @private
 *
 * @param {Object} task - выполняемая задача
 * @param {Object} currentItems - актуальный состав
 *
 * @return {Object} current_items - пропатченный состав корзины
 */
CartTasks.prototype.remove_items = function(task, currentItems) {
  const self = this;

  self.processWithAccessories(task.items, currentItems, (currentItem, taskItem, variantId) => {
    // Если нашли вариант с таким набором опций и у него есть остаток, то отнимаем количество от него
    if (currentItem && currentItem.quantity) {
      const result_quantity = _toFinite(currentItem.quantity) - _toFinite(taskItem.quantity);

      currentItem.quantity = result_quantity > 0 ? result_quantity : 0;
    } else {
      // Если такого варианта с опциями нет, то отнимаем количество от обычного варианта без опций
      const result_quantity = _toFinite(currentItems[variantId]) - _toFinite(taskItem.quantity);

      currentItems[variantId] = result_quantity > 0 ? result_quantity : 0;
    }
  });

  self.updateItemQuantities(task, currentItems, (currentQuantity, quantity) => {
    const result_quantity = _toFinite(currentQuantity) - _toFinite(quantity);

    return result_quantity > 0 ? result_quantity : 0;
  });

  return currentItems;
};

/**
 * @method
 * @private
 *
 * @description
 * Инициализация, читаем штамп, когда корзина была записана. Если в localForage нет этого ключа, или он не совпадает с ключом кук, то передаем в корзину, иначе оставляем в покое
 *
 * @return {void}
 */
CartTasks.prototype._init = function() {
  const self = this;
  self.warehouse_task_waiting = false;

  Store.getItem(`cart-${self._currency_code}`)
    .then(function(cartAtStore) {
      const _insOVCached = _get(cartAtStore, 'ins_order_version', '');
      const notPageCart = window.location.pathname !== '/cart_items';
      const notPageOrder = window.location.pathname !== '/new_order';
      const notPageClient = window.location.pathname.indexOf('/client_account/') === -1;

      if (_insOVCached == cookie('ins_order_version') && notPageCart && notPageClient && notPageOrder) {
        self._done(cartAtStore, true);

        return;
      }

      ajax.get()
        .done(function(order) {
          self._done(order);
        });
    });

  return;
};

/**
 * @description
 * Точка входа
 *
 * Если параметр передан, то добавляем таску в очередь.
 *
 * Если нет - пинаем оставшуюся очередь, может прилететь только от CART()!!!
 * После получения ответа от сервера.
 *
 * @param {Object} task - объект с описанием задачи
 *
 * @return {void}
 */
CartTasks.prototype.send = function(task) {
  const self = this;

  if (task) {
    self._add(task);
  } else {
    self._push();
  }

  return;
};

/**
 * @private
 *
 * @description
 * Добавляем таску в очередь
 *
 * @param {Object} task - объект с описанием задачи
 *
 * @return {void}
 */
CartTasks.prototype._add = function(task) {
  const self = this;

  self._taskToWork.push(task);

  self._push();

  return;
};

/**
 * @method
 * @private
 *
 * @description
 * Пушим очередь на сервер
 *
 * @return {void}
 */
CartTasks.prototype._push = function() {
  const self = this;
  const tasks = self._taskToWork;

  let items_set = self._owner.order.getItems();

  const past_order = _cloneDeep(items_set);
  const past_order_lines = self._getCurrentItems(past_order);

  const result_task = { comments: self._owner.order.getComments() };
  let result_set = [];

  // Если залокано запросом, то выходим из метода
  if (self._lock || tasks.length === 0) {
    return false;
  }

  // Если не залочен, то ставим лок
  self._lock = true;

  // Перебрасываем накопившиеся таски в обработку
  self._taskInWork = self._taskToWork;
  self._taskToWork = [];

  _forEach(self._taskInWork, function(task) {
    // Применяем таски на актуальный состав
    items_set = self._task(task, items_set);

    // Комбайним комменты и купоны
    _merge(result_task.comments, task.comments);

    // Если есть склады, то собираем их
    if (task.warehouse_id) {
      if (!result_task.warehouse_ids) {
        result_task.warehouse_ids = [];
      }

      result_task.warehouse_ids.push(task.warehouse_id);
    }

    result_task.coupon = task.coupon;
  });

  self.past_order = past_order;
  self.past_order_lines = past_order_lines;

  // Если были склады, то ещё раз проходим по таскам, формируем и шлём отдельные для каждого склада
  if (result_task.warehouse_ids && result_task.warehouse_ids.length) {
    _forEach(self._taskInWork, function(task) {
      let warehouse_items_set = self._owner.order.getItems();
      warehouse_items_set = self._task(task, warehouse_items_set);

      const warehouse_result_set = self._computedDiffs(past_order, warehouse_items_set, task);

      task.retries = 0;

      self.waitingWarehouseTask(() => {
        self._send(warehouse_result_set, task);
      }, task);
    });
  } else {
    result_set = self._computedDiffs(past_order, items_set, result_task);

    return self._send(result_set, result_task);
  }
};

/**
 * @method
 * @private
 * @description
 * Сверяем количество позиции в корзине с тем, что пришло в таске. Если позиции нет в корзине, то добавляем её в результирующий объект. Если товар был, то считаем количество и добавляем в результирующий объект. При этом, работаем с опциями и складами.
 *
 * @param {Object} past_set - состав корзины до изменения
 * @param {Object} current_set - состав корзины после изменения
 * @param {Object} task - задача
 *
 * @returns {Object} result_set - объект с изменениями
 */
CartTasks.prototype._computedDiffs = function(past_set, current_set, task) {
  const self = this;

  let [ new_current_set, new_past_set ] = self._adjustSetsForWarehouse(task, current_set, past_set);

  const result_set = {};

  self._computeAccessoryDiffs(new_current_set, new_past_set, result_set);
  self._computeItemDiffs(new_current_set, new_past_set, result_set, task);

  return result_set;
};

/**
 * @method
 * @private
 * @description
 * Проверяем, есть ли в таске ID склада. Если есть, то расширяем состав корзины для работы со складами и достаём айтемы нужного склада из таски.
 *
 * @param {Object} task - задача
 * @param {Object} current_set - новый состав корзины
 * @param {Object} past_set - состав корзины
 *
 * @returns {Array} [ new_current_set, new_past_set ] - массив с пропатченными составами для работы со складами
 */
CartTasks.prototype._adjustSetsForWarehouse = function(task, current_set, past_set) {
  if (task.warehouse_id) {
    return [
      current_set.multi_orders[task.warehouse_id].items,
      ensurePath(past_set, [ 'multi_orders', task.warehouse_id, 'items' ])
    ];
  }

  return [ current_set, past_set ];
};

/**
 * @method
 * @private
 * @description
 * Проверяем, есть ли в таске позиции с опциями. Если есть, то сверяем их с предыдущим составом корзины. Если позиции в корзине ещё нет, то просто добавляем её в результирующий объект. Если позиция была, то считаем количество и добавляем в результирующий объект.
 *
 * @param {Object} new_current_set - новый состав корзины
 * @param {Object} new_past_set - состав корзины
 * @param {Object} result_set - объект с изменениями
 *
 * @returns {void}
 */
CartTasks.prototype._computeAccessoryDiffs = function(new_current_set, new_past_set, result_set) {
  if (!new_current_set.accessoriable_variant_ids) { return; }

  _forIn(new_current_set.accessoriable_variant_ids, (positions, variant_id) => {
    result_set.accessoriable_variant_ids = result_set.accessoriable_variant_ids || {};
    const past_positions = new_past_set.accessoriable_variant_ids && new_past_set.accessoriable_variant_ids[variant_id];

    if (!past_positions) {
      result_set.accessoriable_variant_ids[variant_id] = positions;

      return;
    }

    result_set.accessoriable_variant_ids[variant_id] = this._computeAccessoryPositionDiffs(positions, past_positions);
  });
};

/**
 * @method
 * @private
 * @description
 * Сверяем количество позиции c набором опций в корзине с тем, что пришло в таске.
 *
 * @param {Array} positions - массив с позициями товара для обновления
 * @param {Array} past_positions - массив с позициями товара из текущей корзины
 *
 * @returns {Object} - объект с изменениями
 */
CartTasks.prototype._computeAccessoryPositionDiffs = function(positions, past_positions) {
  return positions.map(item_with_accessories => {
    const past_item = past_positions.find(item => haveSameKeys(item.accessory_value_ids, item_with_accessories.accessory_value_ids));
    const quantity_diff = past_item ? item_with_accessories.quantity - past_item.quantity : item_with_accessories.quantity;

    return {
      quantity: quantity_diff,
      accessory_value_ids: item_with_accessories.accessory_value_ids
    };
  });
};

/**
 * @method
 * @private
 * @description
 * Сверяем количество обычной позиции в корзине с тем, что пришло в таске. Если такой позиции нет в корзине, то добавляем её в результирующий объект. Если позиция была, то считаем количество и добавляем в результирующий объект.
 *
 * @param {Object} new_current_set - новый состав корзины
 * @param {Object} new_past_set - состав корзины
 * @param {Object} result_set - объект с изменениями
 * @param {Object} task - задача
 *
 * @returns {void}
 */
CartTasks.prototype._computeItemDiffs = function(new_current_set, new_past_set, result_set, task) {
  _forIn(new_current_set, (value, key) => {
    if (key === 'accessoriable_variant_ids') { return; }

    if (task.warehouse_id && new_past_set.multi_orders && new_past_set.multi_orders[task.warehouse_id]) {
      if (!new_past_set.multi_orders[task.warehouse_id][key]) {
        result_set[key] = value;
      }

      return;
    }

    if (!new_past_set[key]) {
      result_set[key] = value;

      return;
    }

    if (new_past_set[key] !== value) {
      result_set[key] = value - new_past_set[key];
    }
  });
};

/**
 * @method
 * @private
 *
 * @description
 * Отсылаем на сервак
 *
 * @param {Object} items_set - текущий состав корзины
 * @param {Object} task - задача
 *
 * @return {void}
 */
CartTasks.prototype._send = function(items_set, task) {
  const self = this;

  self._update(items_set, task);

  return;
};

/**
 * @method
 * @private
 *
 * @description
 * Применяем таски на местность
 *
 * @param {Object} task - задача
 * @param {Object} current_items - текущий состав
 *
 * @return {void}
 */
CartTasks.prototype._task = function(task, current_items) {
  const self = this;

  let method = task.method;

  // если такого метода нет - тягаем обновление корзины.
  if (!_isFunction(self[method])) {
    method = 'get';
  }

  return self[method](task, current_items);
};

/**
 * @method
 * @private
 *
 * @description
 * Действия при успешном обновлении
 *
 * @param {Object} order - ответ с платформы
 * @param {boolean} fromInit - если false, nо обновляем значение ключа с кук
 * @param {Object} variantsForUpdate - список вариантов, которые нужно обновить
 * @param {Function} cb - колбек
 *
 * @return {void}
 */
CartTasks.prototype._done = function(order, fromInit, variantsForUpdate, cb) {
  let self = this;
  let _data = {};

  // Если произошло событие set_items в variantsForUpdate будут id вариантов
  let variantsForUpdateIds = variantsForUpdate ? _keys(variantsForUpdate) : [];
  // Забираем предыдущее состояние корзины
  let currentCart = window.InsalesCommonCart.order.get();

  // Так как количество нам отдают в виде строк, мы меняем его на числа, чтобы избежать в будущем приколов
  _forEach(order.multi_orders, function(warehouse) {
    _map(warehouse.items, function(item) {
      item.quantity = _toNumber(item.quantity);
    });
  });

  // Записываем в текущее состояние склады
  if (order.multi_orders) {
    currentCart.multi_orders = order.multi_orders;
  }

  // Достаём из предыдущей корзины объекты товаров, чтобы не тянуть их из Products.getList
  let cacheProductsArray = currentCart._inited ? currentCart.order_lines.filter(orderLine => {
    if (Boolean(orderLine.product) && !variantsForUpdateIds.includes(_toString(orderLine.id))) {
      let status = true;

      if (!orderLine.product.is_more_than_one_variant) { return status; }

      const productVariantIds = orderLine.product.variants.map(variant => variant.id);

      // При этом проверяем что ID запрашиваемых вариантов совпадают с теми, которые лежат в товаре корзины на случай если у товара в корзине не все варианты. Проверка актуальна если запрашиваются варианты только одного товара. Если это варианты разных товаров, то на этом этапе мы не знаем варианты каких товаров лежат в variantsForUpdateIds, поэтому будем получать false.
      status = variantsForUpdateIds.every(variantId => productVariantIds.includes(_toNumber(variantId)));

      return status;
    }

    return false;
  }) : [];

  // делаем из массива объект
  let productsObject = _reduce(cacheProductsArray, function(result, item) {
    if (item.product) {
      result[item.product_id] = item.product;
    }

    return result;
  }, {});

  let cartIds = _map(order.items, 'product_id');
  // Оставляем для обновления только те товары, которые изменились
  // Старое состояние корзины может быть не валидным, поэтому делаем эту фильтрацию
  let cartIdsForUpdate = cartIds.filter(product_id => {
    return !productsObject[_toString(product_id)];
  });

  // Делаем словарь где каждый ключ это ID варианта, а значение это ID товара из корзины, чтобы запрашивать товары не со всеми вариантами, а только с теми, которые лежат в корзине
  const variantIdsMap = new Map();
  const productsForUpdate = order.items.filter(item => cartIdsForUpdate.includes(item.product_id));

  productsForUpdate.forEach(item => {
    variantIdsMap.set(item.variant_id, item.product_id);
  });

  self._doneLight(order, fromInit, function() {
    // в ответе корзины нет информации о товаре,
    // поэтому нам нужно добавить её вручную
    window.Products.getList(cartIdsForUpdate, variantIdsMap)
      .then(function(updatedProducts) {
        let products = _merge(productsObject, updatedProducts);
        _forEach(order.items, function(item) {
          item.product = products[item.product_id];
        });
        self._owner.order.set(order);
        if (!fromInit) {
          // ставим актуальные данные в корзину
          order.ins_order_version = cookie('ins_order_version');
          const currency_code = order.currency_code || self._currency_code;
          Store.setItem(`cart-${currency_code}`, order);
        }

        _data = _clone(self._owner.order.get());
        const taskLength = self._taskInWork.length;
        _forEach(self._taskInWork, function(task, index) {
          _data.action = task || {};
          _data.action.currentItems = self._getCurrentItems(_data.action.items, _data.action.prevStateCart);
          if (task.method === 'set_coupon') {
            _data.coupon = order.coupon;
          }

          if (_get(task, 'isClear', false)) {
            EventBus.publish('clear_items:insales:cart', _data);
          }

          EventBus.publish(task.method + ':insales:cart', _data);
          if (taskLength === (index + 1) && typeof cb === 'function') {
            cb();
          }
        });

        if (!_data.action) {
          _data.action = {};
          _data.action.method = 'update_items';
        }

        EventBus.publish('update_items:insales:cart', _data);

        // Если есть ошибки связанные с опциями, то шлём событие для компонента опций, чтобы вывести ошибки
        if (_data.errors.length) {
          _data.errors.forEach(function(errorsArray) {
            if (typeof errorsArray !== 'object' || !errorsArray.length) { return; }

            let hasAccessoriesErrors = false;

            errorsArray.forEach(function(errorItem) {
              if (!errorItem) { return; }

              if (errorItem.accessory_id) {
                hasAccessoriesErrors = true;
              }
            });

            if (hasAccessoriesErrors) {
              EventBus.publish('accessories-errors:insales:cart', _data);
            }
          });
        }

        if (taskLength === 0 && typeof cb === 'function') {
          cb();
        }
      });
  });
};

/**
 * @method
 * @private
 *
 * @description
 * Действия при успешном обновлении
 *
 * @param {Object} order - ответ с платформы
 * @param {boolean} fromInit - если false, nо обновляем значение ключа с кук
 * @param {function} done - колбек
 *
 * @return {void}
 */
CartTasks.prototype._doneLight = function(order, fromInit, done) {
  const self = this;
  let _data = {};

  self._owner.order.set(order);
  _data = _clone(self._owner.order.get());
  _forEach(self._taskInWork, function(task) {
    _data.action = task || {};
    if (task.method === 'set_coupon') {
      _data.coupon = order.coupon;
    }

    if (_get(task, 'isClear', false)) {
      EventBus.publish('clear_items:insales:cart:light', _data);
    }

    EventBus.publish(task.method + ':insales:cart:light', _data);
  });

  self._checkError(_data);

  if (!_data.action) {
    _data.action = {};
    _data.action.method = 'update_items';
  }

  EventBus.publish('update_items:insales:cart:light', _data);

  done();
};

/**
 * @method
 * @private
 *
 * @description
 * Действия при фейле
 *
 * @param {Object} response - ответ платформы
 *
 * @return {void}
 */
CartTasks.prototype._fail = function(cb) {
  const self = this;

  // если не прокатило - заливаем обратно таски
  if (!_isEmpty(self._taskInWork)) {
    _concat(self._taskToWork, self._taskInWork);
  }

  if (typeof cb === 'function') {
    cb();
  }

  return;
};

/**
 * @method
 * @private
 *
 * @description
 * Действия "всегда"
 *
 * @return {void}
 */
CartTasks.prototype._always = function() {
  const self = this;

  // снимаем лок
  self._lock = false;

  _forEach(self._taskInWork, self._pubAlways);

  // всё ок, удаляем задачи
  self._taskInWork = [];

  // погнали все кругом
  self.send();

  return;
};

/**
 * @private
 *task
 * @description
 * Дергаем событие "всегда"
 *
 * @param {Object} task - задача
 *
 * @return {void}
 */
CartTasks.prototype._pubAlways = function(task) {
  const _data = { action: task || {} };

  EventBus.publish('always:insales:cart', _data);
};

/**
 * @private
 * @description
 * Проверяем наличие ошибок в ответе
 *
 * @param {Object} cartOrderData - данные корзины
 *
 * @return {void}
 */
CartTasks.prototype._checkError = function(cartOrderData) {
  if (cartOrderData.errors && cartOrderData.errors.length) {
    let eventData = _clone(cartOrderData);

    if (!eventData.action) {
      eventData.action = {};
      eventData.action.method = 'error_discount';
    }

    EventBus.publish('error_discount:insales:cart', eventData);
  }
};

/**
 * @method
 * @private
 *
 * @description
 * Дергаем событие "перед"
 *
 * @return {void}
 */
CartTasks.prototype._before = function() {
  const self = this;
  const eventData = {};

  _forEach(self._taskInWork, function(task) {
    eventData.action = task || {};
    if (task.method != 'init') {
      EventBus.publish('before:insales:cart', eventData);
    }
  });

  return;
};

/**
 * @method
 * @private
 *
 * @description
 * Находим айтемы из таски в актуальном составе корзины
 *
 * @param {Object} itemsTask - текущий состав корзины
 * @param {Object} prevStateCart - предыдущее состояние корзины
 *
 * @return {void}
 */
CartTasks.prototype._getCurrentItems = function(itemsTask, prevStateCart) {
  const self = this;
  const currentItems = [];

  let items = itemsTask || {};
  const prevStateCartLines = (prevStateCart) ? prevStateCart.order_lines : [];
  const orderItems = _concat(self._owner.order.order_lines, self.past_order_lines, prevStateCartLines);
  const pushingItems = {};

  if (_isArray(items)) {
    items = _reduce(items, function(result, value) {
      result[value] = -1;

      return result;
    }, {});
  }

  _forEach(orderItems, function(item) {
    if (!item || pushingItems[item.cart_line_id]) { return; }

    // Если это товар без опций
    if (typeof items[item.variant_id] !== 'undefined' && !item.accessory_value_ids) {
      pushingItems[item.cart_line_id] = true;
      currentItems.push(item);
    }

    // Если товар с опциями
    if (items.accessoriable_variant_ids && items.accessoriable_variant_ids[item.variant_id] && item.accessory_value_ids) {
      _forEach(items.accessoriable_variant_ids[item.variant_id], function(accessoriable_item) {
        if (!accessoriable_item.quantity
          && !haveSameKeys(accessoriable_item.accessory_value_ids, item.accessory_value_ids)) { return; }

        pushingItems[item.cart_line_id] = true;
        currentItems.push(item);
      });
    }
  });

  return currentItems;
};

/**
 * @method
 * @private
 * @param {Function} callback - callback
 * @param {Object} task - task
 * @description
 * Метод для контроля выполнения запроса по одному складу перед отправкой следующего, так как при одновременной отправке данные по складам будут перезатираться. Если не дожидаемся за 5 секунд, то прибиваем интервал и вызываем коллбек, для отправки следующего запроса в очереди
 *
 * @return {void}
 */
CartTasks.prototype.waitingWarehouseTask = function(callback, task) {
  const self = this;
  const interval = setInterval(() => {
    if (self.warehouse_task_waiting && task.retries < 50) {
      task.retries += 1;

      return;
    }

    task.retries = 0;
    clearInterval(interval);
    callback();
  }, 100);
};

/**
 * @method
 * @private
 * @description
 * Вспомогательный метод для проверки, есть ли в таске позиции с опциями. Если да, то ищем в актуальной корзине позицию с таким же набором опций, достаём данные и кидаем их в коллбек функцию для дальнейшей обработки
 *
 * @param {Object} task - задача
 * @param {Object} currentItems - актуальный состав корзины
 * @param {Function} updateQuantityCallback - коллбек
 *
 * @return {void}
 */
CartTasks.prototype.processWithAccessories = function(taskItems, currentItems, updateQuantityCallback) {
  if (!taskItems || !taskItems.accessoriable_variant_ids) { return; }

  currentItems.accessoriable_variant_ids = currentItems.accessoriable_variant_ids || {};

  _forIn(taskItems.accessoriable_variant_ids, (accessoriableVariantItems, variantId) => {
    currentItems.accessoriable_variant_ids[variantId] = currentItems.accessoriable_variant_ids[variantId] || [];

    _forEach(accessoriableVariantItems, (itemwithAccessories) => {
      const current_item_with_accessories = currentItems.accessoriable_variant_ids[variantId].find(current_item =>
        haveSameKeys(current_item.accessory_value_ids, itemwithAccessories.accessory_value_ids)
      );

      updateQuantityCallback(current_item_with_accessories, itemwithAccessories, variantId);
    });
  });
};

/**
 * @method
 * @private
 * @description
 * Вспомогательный метод для перебора и обновления количества позиций в корзине при работе со складами или обычными позициями (без опций). Перебираем позиции, прокидываем данные в коллбек где они обратаываются, получаем результат и записываем в корзину
 *
 * @param {Object} task - задача
 * @param {Object} currentItems - актуальный состав корзины
 * @param {Function} updateQuantity - коллбек
 *
 * @return {void}
 */
CartTasks.prototype.updateItemQuantities = function(task, currentItems, updateQuantity) {
  _forIn(task.items, (quantity, key) => {
    if (key === 'accessoriable_variant_ids') { return; }

    if (task.warehouse_id && currentItems.multi_orders && currentItems.multi_orders[task.warehouse_id]) {
      currentItems.multi_orders[task.warehouse_id].items[key] =
        updateQuantity(currentItems.multi_orders[task.warehouse_id].items[key], quantity);
    } else if (task.warehouse_id) {
      const warehouseItems = ensurePath(currentItems, [ 'multi_orders', task.warehouse_id, 'items' ]);

      warehouseItems[key] = _toFinite(quantity);
    } else {
      currentItems[key] = updateQuantity(currentItems[key], quantity, key);
    }
  });
};

export default CartTasks;
